// @ts-nocheck
import { useState } from 'react';
import { Eye } from 'lucide-react';
import moment from 'moment';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { maskNumber, maskNumberForHangup } from '@/utilities/cliDisplay';
import { pseudoCdrLabel } from '@/utilities/pseudoCdr';
import { missedIvrLabel } from '@/utilities/missedIvr';
import { CAMPAIGN_TYPE_MAP } from '@/utilities/constant';
import PseudoCdrBadge from '@/components/partials/components/PseudoCdrBadge';
import MissedIvrBadge from '@/components/partials/components/MissedIvrBadge';
import CallModeTag, { callModeLabel, isCampaignRow } from '@/components/partials/components/CallModeTag';
import CallRecordingCell from '@/components/partials/components/CallRecordingCell';
import CopyButton from '@/features/api-webhook/components/CopyButton';
import { feedbackExportColumns, FeedbackCell, feedbackOf } from '@/features/call-quality-feedback/feedback';
import { CallTranscriptButton } from '@/features/transcripts/CallTranscript';
import { CALL_STATUS_TEXT, callStatusKey } from './callReportStats';

export const CALL_REPORT_COLUMN_IDS = [
  'details', 'customer_number', 'status', 'call_type', 'did', 'agent', 'callTimings', 'callDuration',
  'callGroupIvr', 'recording', 'campaign', 'user', 'trace_id', 'pseudo', 'call_quality_feedback',
  'transcript',
] as const;

export const formatDateTime = (value: any) => {
  if (!value) return '';
  const m = moment(String(value), ['YYYY-MM-DD HH:mm:ss', moment.ISO_8601], true);
  return m.isValid() ? m.format('YYYY-MM-DD HH:mm:ss') : '';
};

const num = (value: any) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const lastTen = (value: any) => String(value ?? '').replace(/\D/g, '').slice(-10);
export const numbersMatch = (a: any, b: any) => Boolean(lastTen(a) && lastTen(a) === lastTen(b));

export const customerNumber = (row: any) =>
  row.call_type === 'outgoing' &&
  (numbersMatch(row.caller_number, row.did) ||
    (row.did != null && String(row.caller_number ?? '') === maskNumber(String(row.did))))
    ? row.callee_number
    : row.caller_number;

export const totalDuration = (row: any) => {
  if (row?.total_duration != null && row.total_duration !== '') return num(row.total_duration);
  const t = (v: any) => (v ? new Date(String(v).replace(' ', 'T') + 'Z').getTime() : NaN);
  const start = t(row?.start_time);
  const end = t(row?.end_time);
  if (Number.isNaN(start) || Number.isNaN(end)) return num(row?.billing_duration) + num(row?.patch_duration);
  return Math.max(0, Math.round((end - start) / 1000));
};

export const callGroupOf = (row: any) => String(row?.queue_name || row?.call_group_name || '').trim();
const holdOf = (row: any) => num(row.total_hold_duration ?? row.hold_duration);
const isOutgoing = (row: any) => row?.call_type === 'outgoing';
const campaignType = (row: any) => CAMPAIGN_TYPE_MAP[String(row?.campaign_type)] || '';

const PILL = 'inline-flex items-center whitespace-nowrap rounded-full px-2.5 py-0.5 text-[11px] font-bold uppercase ring-1 ring-inset ';
const STATUS_PILL = {
  connected: 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300',
  failed: 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300',
  abandoned: 'bg-amber-100 text-amber-700 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300',
};
const COPY_ICON = 'ml-1 h-5 w-5 p-0 align-middle [&_svg]:h-3 [&_svg]:w-3';

const callTypeBadge = (row: any) => {
  const incoming = row.call_type === 'incoming';
  return (
    <span
      className={
        'inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-semibold ring-1 ring-inset ' +
        (incoming
          ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
          : 'bg-violet-100 text-violet-700 ring-violet-200 dark:bg-violet-950/40 dark:text-violet-300')
      }
    >
      {incoming ? 'Incoming' : 'Outgoing'}
    </span>
  );
};

const stacked = (primary: any, secondary?: any, extra?: any) => (
  <span className="flex flex-col leading-tight">
    <span className="font-semibold">{primary || '-'}</span>
    {secondary ? <span className="text-[11px] text-muted-foreground">{secondary}</span> : null}
    {extra ? <span className="break-all text-[11px] text-muted-foreground">{extra}</span> : null}
  </span>
);

const labelLine = (label: string, value: any) => (
  <span className="whitespace-nowrap">
    <span className="text-muted-foreground">{label}: </span>
    {value}
  </span>
);

export type ColumnOpts = {
  hangupLabel: (code: any) => string;
  onView: (row: any) => void;
  canViewRecordings: boolean;
  canViewFeedback: boolean;
  canViewTranscripts: boolean;
  transcriptLineCount: (traceId: any) => number;
  /** Campaign report only: Customer Details, Retry Count, IVR Name, Agents Dialed, Pulses, Trunk. */
  campaignExtras?: boolean;
  /** Pulses column: only when the rows carry a breakdown (the backend strips it for CC roles). */
  showPulses?: boolean;
  /** SA / sub-SA: pulses per company instead of the sum. */
  showCompanyNames?: boolean;
};

const agentsDialedText = (r: any) =>
  (Array.isArray(r.agents_dialed) ? r.agents_dialed : [])
    .map((a) => `${a.agent_name || a.agent || '-'}${a.state ? ` (${a.state})` : ''}`)
    .join(', ');
const agentsDialedCount = (r: any) => Number(r.agents_dialed_count ?? r.agents_dialed?.length ?? 0);
// Absent breakdown = no billing access (stripped by the backend for CC roles) -> '-'.
const pulsesText = (r: any, perCompany?: boolean) => {
  const list = Array.isArray(r.pulses_breakdown) ? r.pulses_breakdown : [];
  if (!list.length) return '-';
  if (perCompany) return list.map((e) => `${e.company_name || e.company_id}: ${e.value}`).join('; ');
  return String(Number(list.reduce((sum, e) => sum + (Number(e.value) || 0), 0).toFixed(4)));
};

/**
 * Customer Details text with each contact's custom fields appended to its line. The backend sends
 * customer_additional_details one line per contact, in the same order (empty line = none).
 */
export const withAdditionalDetails = (details: unknown, additional: unknown): string => {
  const extra = String(additional || '').split('\n');
  return String(details || '')
    .split('\n')
    .map((line, i) => (line && extra[i] ? `${line} | ${extra[i]}` : line))
    .join('\n');
};

export const parseCustomerDetails = (text: string): Record<string, string>[] =>
  text.split('\n').filter(Boolean).map((line) => {
    const f: Record<string, string> = {};
    for (const part of line.split(' | ')) {
      const i = part.indexOf(': ');
      const value = i < 0 ? '' : part.slice(i + 2).trim();
      if (i > 0 && value && value !== '-') f[part.slice(0, i)] = value;
    }
    return f;
  });

const contactName = (c: Record<string, string>) => [c['First Name'], c['Last Name']].filter(Boolean).join(' ');

/** Name + primary number of the first matched contact; everything (custom fields too) in a modal. */
function CustomerDetailsCell({ row }: { row: any }) {
  const [open, setOpen] = useState(false);
  const contacts = parseCustomerDetails(withAdditionalDetails(row.customer_details, row.customer_additional_details));
  if (!contacts.length) return <>-</>;
  const c = contacts[0];
  return (
    <div className="min-w-[160px] max-w-[240px] space-y-0.5 whitespace-normal text-xs">
      <div className="font-medium">{contactName(c) || '-'}</div>
      {c['Primary Number'] && <div className="text-muted-foreground">{c['Primary Number']}</div>}
      {contacts.length > 1 && <div className="text-muted-foreground">+{contacts.length - 1} more contact(s)</div>}
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="mt-1 inline-flex items-center gap-1 whitespace-nowrap rounded-md bg-primary/10 px-2 py-0.5 text-[11px] font-semibold text-primary ring-1 ring-inset ring-primary/20 transition hover:bg-primary/15"
      >
        <Eye className="h-3 w-3" /> View full details
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Customer Details</DialogTitle>
            <DialogDescription>
              {contacts.length > 1 ? `${contacts.length} saved contacts match this number` : contactName(c) || 'Saved contact'}
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] space-y-3 overflow-y-auto">
            {contacts.map((contact, i) => (
              <div key={i} className="overflow-hidden rounded-lg border border-border/60">
                {contacts.length > 1 && (
                  <div className="bg-muted px-3 py-1.5 text-xs font-semibold">
                    Contact {i + 1}{contactName(contact) ? ` · ${contactName(contact)}` : ''}
                  </div>
                )}
                <table className="w-full text-sm">
                  <tbody>
                    {Object.entries(contact).map(([k, v]) => (
                      <tr key={k} className="border-t border-border/50 align-top first:border-t-0">
                        <td className="w-2/5 px-3 py-1.5 font-medium text-foreground">{k}</td>
                        <td className="whitespace-pre-wrap break-words px-3 py-1.5 text-muted-foreground">{v}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Custom fields (customer_additional_details) are shown inside this column's modal, not a column of their own.
const customerDetailsColumn = {
  id: 'customer_details',
  header: 'Customer Details',
  accessorFn: (r) => withAdditionalDetails(r.customer_details, r.customer_additional_details),
  enableSorting: false,
  cell: ({ row }) => (row.original.customer_details ? <CustomerDetailsCell row={row.original} /> : '-'),
};

function campaignExtraColumns(o: ColumnOpts) {
  if (!o.campaignExtras) return [];
  const muted = (v: any) => <span className="text-xs text-muted-foreground">{v || '-'}</span>;
  return [
    customerDetailsColumn,
    { id: 'retry_attempt', header: 'Retry', accessorFn: (r) => r.retry_attempt ?? '', size: 70, cell: ({ row }) => row.original.retry_attempt ?? '-' },
    { id: 'ivr_name', header: 'IVR Name', accessorFn: (r) => r.ivr_name || '', cell: ({ row }) => muted(row.original.ivr_name) },
    {
      id: 'agents_dialed',
      header: 'Agents Dialed',
      accessorFn: (r) => agentsDialedCount(r),
      cell: ({ row }) => {
        const text = agentsDialedText(row.original);
        const count = agentsDialedCount(row.original);
        if (!count && !text) return '-';
        return (
          <div className="max-w-[240px] truncate text-xs" title={text}>
            <span className="font-semibold">{count}</span>
            {text ? ` · ${text}` : ''}
          </div>
        );
      },
    },
    ...(o.showPulses
      ? [
          {
            id: 'pulses',
            header: 'Pulses',
            enableSorting: false,
            size: 90,
            cell: ({ row }) => <span className="tabular-nums">{pulsesText(row.original, o.showCompanyNames)}</span>,
          },
        ]
      : []),
    { id: 'trunk', header: 'Trunk', accessorFn: (r) => r.trunk || '', cell: ({ row }) => muted(row.original.trunk) },
  ];
}

export function buildCallReportColumns(o: ColumnOpts) {
  const masked = (value: any, r: any) => maskNumberForHangup(value ?? '', r.hangup_cause) || '';
  const cols = [
    {
      id: 'details',
      header: 'View',
      enableSorting: false,
      size: 80,
      cell: ({ row }) => (
        <Button type="button" variant="outline" size="sm" className="h-8 gap-1 px-2" onClick={() => o.onView(row.original)}>
          <Eye size={14} /> View
        </Button>
      ),
    },
    {
      id: 'customer_number',
      header: 'Customer',
      accessorFn: (r) => customerNumber(r) ?? '',
      cell: ({ row }) => masked(customerNumber(row.original), row.original) || '-',
    },
    ...(o.campaignExtras ? [] : [customerDetailsColumn]),
    {
      id: 'status',
      header: 'Call Status',
      size: 200,
      accessorFn: (r) => [CALL_STATUS_TEXT[callStatusKey(r)], o.hangupLabel(r.hangup_cause), r.call_failure_reason].filter(Boolean).join(' '),
      cell: ({ row }) => {
        const r = row.original;
        const key = callStatusKey(r);
        return (
          <span className="flex flex-col items-start gap-0.5 text-xs leading-tight">
            <span className={PILL + STATUS_PILL[key]}>{CALL_STATUS_TEXT[key]}</span>
            {r.hangup_cause ? <span>{o.hangupLabel(r.hangup_cause)}</span> : null}
            {r.call_failure_reason ? (
              <span className="font-medium text-rose-600 dark:text-rose-400">{r.call_failure_reason}</span>
            ) : null}
            <MissedIvrBadge row={r} />
          </span>
        );
      },
    },
    {
      id: 'call_type',
      header: 'Call Type',
      accessorFn: (r) => `${r.call_type} ${callModeLabel(r, isOutgoing(r))}`,
      cell: ({ row }) => (
        <span className="flex flex-col items-start gap-1">
          {callTypeBadge(row.original)}
          <CallModeTag row={row.original} isOutgoing={isOutgoing(row.original)} />
        </span>
      ),
    },
    {
      accessorKey: 'did',
      header: 'DID',
      cell: ({ row }) => masked(row.original.did, row.original) || '-',
    },
    {
      id: 'agent',
      header: 'Agent',
      size: 150,
      accessorFn: (r) => `${r.agent_name ?? ''} ${r.agent_number ?? ''} ${r.agent_email ?? ''}`.trim(),
      cell: ({ row }) => stacked(row.original.agent_name, masked(row.original.agent_number, row.original), row.original.agent_email),
    },
    {
      id: 'callTimings',
      header: 'Call Timings',
      size: 190,
      accessorFn: (r) => r.start_time || '',
      cell: ({ row }) => (
        <span className="flex flex-col text-xs leading-tight">
          {labelLine('Start', formatDateTime(row.original.start_time))}
          {labelLine('End', formatDateTime(row.original.end_time))}
        </span>
      ),
    },
    {
      id: 'callDuration',
      // Row = customer (A_PARTY) leg, so every duration is the customer's (user 2026-10-29).
      header: 'Customer Call Duration',
      size: 150,
      accessorFn: (r) => totalDuration(r),
      cell: ({ row }) => {
        const r = row.original;
        return (
          <span className="flex flex-col text-xs leading-tight">
            {labelLine('Total', `${totalDuration(r)}s`)}
            {labelLine('Talk Time', `${num(r.patch_duration)}s`)}
            {labelLine('Ring', `${num(r.bridge_ring_duration)}s`)}
            {labelLine('Hold', `${holdOf(r)}s`)}
            {labelLine('Mute', `${num(r.mute_duration)}s`)}
          </span>
        );
      },
    },
    {
      id: 'callGroupIvr',
      header: 'Call Group / IVR',
      size: 160,
      accessorFn: (r) => callGroupOf(r) || r.ivr_name || '',
      cell: ({ row }) => {
        const group = callGroupOf(row.original);
        const ivr = row.original.ivr_name;
        return stacked(group || ivr, group ? 'Call Group' : ivr ? 'IVR' : null);
      },
    },
    ...(o.canViewRecordings
      ? [
          {
            id: 'recording',
            header: 'Recordings & Voicemails',
            enableSorting: false,
            cell: ({ row }) => <CallRecordingCell recordingFile={row.original.recording_file} />,
          },
        ]
      : []),
    {
      id: 'campaign',
      header: 'Campaign',
      accessorFn: (r) => `${r.campaign_name || ''} ${campaignType(r)}`.trim(),
      cell: ({ row }) => (row.original.campaign_name ? stacked(row.original.campaign_name, campaignType(row.original)) : '-'),
    },
    {
      id: 'user',
      header: 'User',
      accessorFn: (r) => `${r.user_id ?? ''} ${r.user_username || r.username || ''}`.trim(),
      cell: ({ row }) => stacked(row.original.user_id ?? '-', row.original.user_username || row.original.username),
    },
    {
      accessorKey: 'trace_id',
      header: 'Trace ID',
      size: 190,
      cell: ({ row }) =>
        row.original.trace_id ? (
          <span className="whitespace-nowrap font-mono text-xs">
            {row.original.trace_id}
            <CopyButton text={String(row.original.trace_id)} className={COPY_ICON} />
          </span>
        ) : (
          '-'
        ),
    },
    {
      id: 'pseudo',
      header: 'Pseudo CDR',
      accessorFn: (r) => pseudoCdrLabel(r),
      cell: ({ row }) => <PseudoCdrBadge row={row.original} />,
    },
    ...(o.canViewFeedback
      ? [
          {
            id: 'call_quality_feedback',
            header: 'Audio Feedback',
            size: 130,
            accessorFn: (r) => feedbackOf(r)[0]?.rating ?? '',
            cell: ({ row }) => <FeedbackCell row={row.original} />,
          },
        ]
      : []),
    ...(o.canViewTranscripts
      ? [
          {
            id: 'transcript',
            header: 'Transcript',
            enableSorting: false,
            size: 90,
            cell: ({ row }) => (
              <CallTranscriptButton
                traceId={row.original.trace_id}
                lineCount={o.transcriptLineCount(row.original.trace_id)}
                className="h-8 w-8"
              />
            ),
          },
        ]
      : []),
    ...campaignExtraColumns(o),
  ];
  return cols;
}

/** Export columns per table column (§4): one value per cell. */
export function callReportExportSplit(o: ColumnOpts, mediaUrls: (row: any, type: string) => string) {
  const masked = (value: any, r: any) => maskNumberForHangup(value ?? '', r.hangup_cause) || '';
  const feedback = (r: any, key: string) => feedbackExportColumns(feedbackOf(r))[key];
  return {
    customer_number: [['Customer', (r) => masked(customerNumber(r), r)]],
    status: [
      ['Call Status', (r) => CALL_STATUS_TEXT[callStatusKey(r)]],
      ['Hangup Reason', (r) => (r.hangup_cause ? o.hangupLabel(r.hangup_cause) : '')],
      ['Failure Reason', (r) => r.call_failure_reason],
      ['Missed IVR', (r) => missedIvrLabel(r)],
    ],
    call_type: [
      ['Call Type', (r) => (r.call_type === 'incoming' ? 'Incoming' : 'Outgoing')],
      ['Call Mode', (r) => (!isOutgoing(r) ? 'Incoming' : isCampaignRow(r) ? 'Auto Dial' : callModeLabel(r, true))],
    ],
    did: [['DID', (r) => masked(r.did, r)]],
    agent: [
      ['Agent Name', (r) => r.agent_name],
      ['Agent Number', (r) => masked(r.agent_number, r)],
      ['Agent Email', (r) => r.agent_email],
    ],
    callTimings: [
      ['Start Time', (r) => formatDateTime(r.start_time)],
      ['End Time', (r) => formatDateTime(r.end_time)],
    ],
    callDuration: [
      ['Customer Total (s)', (r) => totalDuration(r)],
      ['Customer Talk Time (s)', (r) => num(r.patch_duration)],
      ['Customer Ring (s)', (r) => num(r.bridge_ring_duration)],
      ['Customer Hold (s)', (r) => holdOf(r)],
      ['Customer Mute (s)', (r) => num(r.mute_duration)],
    ],
    callGroupIvr: [
      ['Call Group', (r) => callGroupOf(r)],
      ['IVR', (r) => (callGroupOf(r) ? '' : r.ivr_name)],
    ],
    recording: [
      ['Recordings', (r) => mediaUrls(r, 'Recording')],
      ['Voicemails', (r) => mediaUrls(r, 'Voicemail')],
    ],
    campaign: [
      ['Campaign ID', (r) => (Number(r.campaign_id) > 0 ? Number(r.campaign_id) : '')],
      ['Campaign Name', (r) => r.campaign_name],
      ['Campaign Type', (r) => campaignType(r)],
    ],
    user: [
      ['User ID', (r) => r.user_id],
      ['Username', (r) => r.user_username || r.username],
    ],
    trace_id: [['Trace ID', (r) => r.trace_id]],
    pseudo: [],
    call_quality_feedback: [
      ['Call Audio Rating', (r) => feedback(r, 'Call Audio Rating')],
      ['Call Audio Issues', (r) => feedback(r, 'Call Audio Issues')],
    ],
    details: [],
    transcript: [],
    // Custom fields go on each contact's Customer Details line, not in a column of their own.
    customer_details: [['Customer Details', (r) => withAdditionalDetails(r.customer_details, r.customer_additional_details)]],
    customer_additional_details: [],
    retry_attempt: [['Retry Count', (r) => r.retry_attempt ?? 0]],
    ivr_name: [['IVR Name', (r) => r.ivr_name]],
    agents_dialed: [
      ['Agents Dialed Count', (r) => agentsDialedCount(r)],
      ['Agents Dialed', (r) => agentsDialedText(r)],
    ],
    pulses: [['Total Pulses', (r) => pulsesText(r, o.showCompanyNames)]],
    trunk: [['Trunk', (r) => r.trunk]],
  };
}
